// ABOUTME: Modal component for celebrating completed goals with congratulations messages, achievement details, and reward display
// ABOUTME: Provides a celebratory experience when parents mark goals as celebrated, with confetti animation and child-friendly messaging

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Celebration, EmojiEvents, CardGiftcard } from '@mui/icons-material';
import { Goal, GoalType } from '../types/api';
import { goalsAPI } from '../services/api';
import { getFullApiUrl } from '../utils/routeUtils';
import { celebrationGold, celebrationGoldHover, decorativeGradients } from '../theme/tokens';

interface CelebrationModalProps {
  isOpen: boolean;
  goal: Goal | null;
  childName: string;
  onClose: () => void;
  onCelebrated: (celebratedGoal: Goal) => void;
}

const CelebrationModal: React.FC<CelebrationModalProps> = ({
  isOpen,
  goal,
  childName,
  onClose,
  onCelebrated,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!goal) return null;

  const getAchievementMessage = (goal: Goal): string => {
    switch (goal.type) {
      case GoalType.HeartStreak: {
        const days = goal.targetValue;
        return `You got hearts ${days} days in a row!`;
      }
      case GoalType.StarCollection:
        return `You collected ${goal.targetValue} stars!`;
      case GoalType.HeartCollection:
        return `You collected ${goal.targetValue} hearts!`;
      default:
        return `You completed this goal!`;
    }
  };

  const handleCelebrate = async () => {
    if (!goal) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const celebratedGoal = await goalsAPI.celebrateGoal(goal.id);
      onCelebrated(celebratedGoal);
    } catch (error) {
      console.error('Failed to celebrate goal:', error);
      setError('Failed to celebrate goal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'visible',
          background: decorativeGradients.heroPurple,
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Celebration sx={{ fontSize: 48, color: celebrationGold }} />
        </Box>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Congratulations {childName}!
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        <Box sx={{ mb: 3 }}>
          <EmojiEvents sx={{ fontSize: 36, color: celebrationGold, mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {getAchievementMessage(goal)}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Amazing work on completing: <strong>{goal.title}</strong>
          </Typography>
        </Box>

        {(goal.rewardDescription || goal.rewardImageUrl) && (
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.common.white, 0.1),
              borderRadius: 2,
              p: 2,
              mb: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <CardGiftcard sx={{ fontSize: 24, mr: 1, color: celebrationGold }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Reward:
              </Typography>
            </Box>
            
            {goal.rewardImageUrl && (
              <Avatar
                src={getFullApiUrl(goal.rewardImageUrl)}
                alt="Reward image"
                sx={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`,
                }}
              />
            )}
            
            {goal.rewardDescription && (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {goal.rewardDescription}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: alpha(theme.palette.common.white, 0.9) }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
          🎉 Time to celebrate this awesome achievement! 🎉
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: alpha(theme.palette.common.white, 0.5),
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: alpha(theme.palette.common.white, 0.1),
            },
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCelebrate}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: celebrationGold,
            color: theme.palette.text.primary,
            fontWeight: 600,
            minWidth: 140,
            '&:hover': {
              backgroundColor: celebrationGoldHover,
            },
            '&:disabled': {
              backgroundColor: alpha(celebrationGold, 0.6),
            },
          }}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} sx={{ color: theme.palette.text.primary }} />
            ) : (
              <Celebration />
            )
          }
        >
          {isLoading ? 'Celebrating...' : 'Mark as Celebrated'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CelebrationModal;